import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProjectDetailsReport } from "reduxStore/action/webReportActions";
import { getPdrReport } from "reduxStore/action/reportActions";
import TableDataNotFound from "component/TableDataNotFound";
import strings from "translate/language";
import Breadcrumb from "component/common/breadcrumb";
import Pagination from "component/common/Pagination";
import { handleListResponse, seo } from "utils/utils";
import Select from "react-select";
import moment from "moment";
import * as XLSX from "xlsx"; // Importing the xlsx library
import ExcelJS from "exceljs";
import { Slack } from "react-feather";
import { getAgentProperty } from "reduxStore/action/propertyActions";

const PdrReport = ({ isAuth = false }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState({
    on_date: new Date(),
    property_id: "",
  });

  useEffect(() => {
    dispatch(getPdrReport({}));
    dispatch(getAgentProperty({ offset: 0, limit: "" }));
  }, []);

  const [propertyList, setPropertyList] = useState([]);

  const propertySelector = useSelector((state) => state.propertyData);
  useEffect(() => {
    if (propertySelector.response) {
      if (
        propertySelector?.response &&
        propertySelector?.response.status === 200
      ) {
        const filteredData = propertySelector?.response?.data?.filter(
          (item) => item.status === true
        );
        if (filteredData?.length > 0) {
          setPropertyList(handleListResponse(filteredData));
        } else {
          setPropertyList([]); // Clear the list if no valid data
        }
      } else {
        setPropertyList([]);
      }
    }
  }, [dispatch, propertySelector]);

  const [formValid, setFormValid] = useState({ on_date: "" });
  const { pdrReport } = useSelector((state) => state.report);

  useEffect(() => {
    seo({
      title: "Report Project wise",
      metaDescription: "All Project",
      canonicalLink: window.location.href,
    });
    let interval = setInterval(() => {}, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const propertyData = pdrReport?.data;

  const fetchProjectReport = () => {
    if (validateForm()) {
      setFormValid({ on_date: "" });
      dispatch(getPdrReport(form));
    }
  };

  const handleInputField = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (selectedOption) => {
    setForm({ ...form, property_id: selectedOption });
  };

  const validateForm = () => {
    if (!form.on_date) {
      setFormValid({ on_date: "Please fill in the date" });
      return false;
    }
    return true;
  };

  const filterDetail = () => {
    fetchProjectReport();
  };

  const filterReset = () => {
    setForm({ on_date: new Date(), property_id: "" });
    dispatch(getPdrReport({}));
    // Clear local storage on reset
  };

  const formatDate = (dateString) => {
    if (!dateString) return ""; // Return empty if no date

    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0"); // Get day and pad with zero if needed
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Get month (0-indexed) and pad with zero
    const year = String(date.getFullYear()).slice(-2); // Get last two digits of year

    return `${day}/${month}/${year}`; // Return formatted date
  };

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Property Report");

    // Set headers for the Excel file
    worksheet.columns = [
      { header: "Property Title", key: "PropertyTitle", width: 50 },
      { header: "FTD", key: "FTD", width: 40 },
      { header: "MTD", key: "MTD", width: 40 },
      { header: "YTD", key: "YTD", width: 40 },
    ];

    let data = [];

    // Iterate over property data to fill in the data array
    propertyData.forEach((property) => {
      const ftdBookingData = property.bookings[0]?.ftd || {
        GrossBookings: 0,
        Cancellation: 0,
        NetBookings: 0,
      };
      const mtdBookingData = property.bookings[0]?.mtd || {
        GrossBookings: 0,
        Cancellation: 0,
        NetBookings: 0,
      };
      const ytdBookingData = property.bookings[0]?.ytd || {
        GrossBookings: 0,
        Cancellation: 0,
        NetBookings: 0,
      };

      const ftdSiteVisitData = property.siteVisits[0]?.ftd || {
        SiteVisits: 0,
        Revisits: 0,
      };
      const mtdSiteVisitData = property.siteVisits[0]?.mtd || {
        SiteVisits: 0,
        Revisits: 0,
      };
      const ytdSiteVisitData = property.siteVisits[0]?.ytd || {
        SiteVisits: 0,
        Revisits: 0,
      };

      // Add Property Title
      data.push({
        PropertyTitle: `Property Title: ${property.property_name}`,
        FTD: "",
        MTD: "",
        YTD: "",
      });

      // Add Bookings Section
      data.push({
        PropertyTitle: "Bookings",
        FTD: "FTD",
        MTD: "MTD",
        YTD: "YTD",
      });
      data.push({
        PropertyTitle: "Gross Bookings",
        FTD: ftdBookingData.GrossBookings,
        MTD: mtdBookingData.GrossBookings,
        YTD: ytdBookingData.GrossBookings,
      });
      data.push({
        PropertyTitle: "Cancellations",
        FTD: ftdBookingData.Cancellation,
        MTD: mtdBookingData.Cancellation,
        YTD: ytdBookingData.Cancellation,
      });
      data.push({
        PropertyTitle: "Net Bookings",
        FTD: ftdBookingData.NetBookings,
        MTD: mtdBookingData.NetBookings,
        YTD: ytdBookingData.NetBookings,
      });

      // Add Site Visits Section
      data.push({ PropertyTitle: "Site Visits", FTD: "", MTD: "", YTD: "" });
      data.push({
        PropertyTitle: "Total Site Visits",
        FTD: ftdSiteVisitData.SiteVisits,
        MTD: mtdSiteVisitData.SiteVisits,
        YTD: ytdSiteVisitData.SiteVisits,
      });
      data.push({
        PropertyTitle: "Channel Partner Visits",
        FTD: ftdSiteVisitData.ChannelPartnerVisitsCount,
        MTD: mtdSiteVisitData.ChannelPartnerVisitsCount,
        YTD: ytdSiteVisitData.ChannelPartnerVisitsCount,
      });
      data.push({
        PropertyTitle: "Direct Walk-In Visits",
        FTD: ftdSiteVisitData.DirectWalkInVisitsCount,
        MTD: mtdSiteVisitData.DirectWalkInVisitsCount,
        YTD: ytdSiteVisitData.DirectWalkInVisitsCount,
      });
      data.push({
        PropertyTitle: "Referral Partner Visits",
        FTD: ftdSiteVisitData.ReferralPartnerVisitsCount,
        MTD: mtdSiteVisitData.ReferralPartnerVisitsCount,
        YTD: ytdSiteVisitData.ReferralPartnerVisitsCount,
      });
      data.push({
        PropertyTitle: "Reference Visits",
        FTD: ftdSiteVisitData.ReferenceVisitsCount,
        MTD: mtdSiteVisitData.ReferenceVisitsCount,
        YTD: ytdSiteVisitData.ReferenceVisitsCount,
      });

      data.push({
        PropertyTitle: "Pre-Sales Visits",
        FTD: ftdSiteVisitData.PreSalesVisitsCount,
        MTD: mtdSiteVisitData.PreSalesVisitsCount,
        YTD: ytdSiteVisitData.PreSalesVisitsCount,
      });
      data.push({
        PropertyTitle: "Exhibition Visits",
        FTD: ftdSiteVisitData.ExhibitionVisitsCount,
        MTD: mtdSiteVisitData.ExhibitionVisitsCount,
        YTD: ytdSiteVisitData.ExhibitionVisitsCount,
      });
      data.push({
        PropertyTitle: "Other Visits",
        FTD: ftdSiteVisitData.OtherVisitsCount,
        MTD: mtdSiteVisitData.OtherVisitsCount,
        YTD: ytdSiteVisitData.OtherVisitsCount,
      });

      // Add Site Revisits Section
      data.push({ PropertyTitle: "Site Revisits", FTD: "", MTD: "", YTD: "" });
      data.push({
        PropertyTitle: "Revisits",
        FTD: ftdSiteVisitData.Revisits,
        MTD: mtdSiteVisitData.Revisits,
        YTD: ytdSiteVisitData.Revisits,
      });
      data.push({
        PropertyTitle: "Channel Partner Revisits",
        FTD: ftdSiteVisitData.ChannelPartnerRevisitsCount,
        MTD: mtdSiteVisitData.ChannelPartnerRevisitsCount,
        YTD: ytdSiteVisitData.ChannelPartnerRevisitsCount,
      });
      data.push({
        PropertyTitle: "Direct Walk-In Revisits",
        FTD: ftdSiteVisitData.DirectWalkInRevisitsCount,
        MTD: mtdSiteVisitData.DirectWalkInRevisitsCount,
        YTD: ytdSiteVisitData.DirectWalkInRevisitsCount,
      });
      data.push({
        PropertyTitle: "Referral Partner Revisits",
        FTD: ftdSiteVisitData.ReferralPartnerRevisitsCount,
        MTD: mtdSiteVisitData.ReferralPartnerRevisitsCount,
        YTD: ytdSiteVisitData.ReferralPartnerRevisitsCount,
      });
      data.push({
        PropertyTitle: "Reference Revisits",
        FTD: ftdSiteVisitData.ReferenceRevisitsCount,
        MTD: mtdSiteVisitData.ReferenceRevisitsCount,
        YTD: ytdSiteVisitData.ReferenceRevisitsCount,
      });

      data.push({
        PropertyTitle: "Pre-Sales Revisits",
        FTD: ftdSiteVisitData.PreSalesRevisitsCount,
        MTD: mtdSiteVisitData.PreSalesRevisitsCount,
        YTD: ytdSiteVisitData.PreSalesRevisitsCount,
      });
      data.push({
        PropertyTitle: "Exhibition Revisits",
        FTD: ftdSiteVisitData.ExhibitionRevisitsCount,
        MTD: mtdSiteVisitData.ExhibitionRevisitsCount,
        YTD: ytdSiteVisitData.ExhibitionRevisitsCount,
      });
      data.push({
        PropertyTitle: "Other Revisits",
        FTD: ftdSiteVisitData.OtherRevisitsCount,
        MTD: mtdSiteVisitData.OtherRevisitsCount,
        YTD: ytdSiteVisitData.OtherRevisitsCount,
      });

      // Add an empty row for spacing
      data.push({ PropertyTitle: "", FTD: "", MTD: "", YTD: "" });
    });

    // Add all data to the worksheet
    worksheet.addRows(data);

    // Apply borders to all cells
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // Header row styling
    worksheet.getRow(1).font = { bold: true, size: 14 }; // Title row styling
    worksheet.getRow(1).alignment = {
      vertical: "middle",
      horizontal: "center",
    };
    worksheet.mergeCells("A1:D1"); // Merge cells for the title row
    worksheet.getCell("A1").value = `Property Report on date: ${formatDate(
      new Date()
    )}`;
    worksheet.getCell("A1").border = {
      top: { style: "thick" },
      left: { style: "thick" },
      bottom: { style: "thick" },
      right: { style: "thick" },
    };
    worksheet.getRow(1).height = 40;

    // Header cells background and bold font
    worksheet.getRow(3).font = { bold: true };
    worksheet.getRow(3).alignment = {
      vertical: "middle",
      horizontal: "center",
    };
    worksheet.getRow(3).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFDDDDDD" },
    };

    // Style specific values (Bookings, Site Visits, Revisits)
    const valuesToStyle = ["Bookings", "Site Visits", "Site Revisits"];
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        if (valuesToStyle.includes(cell.value)) {
          cell.font = { bold: true };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFDDDDDD" },
          };
        }
        if (cell.value === "Bookings") {
          const cellAbove = worksheet.getCell(
            cell.address.replace(/\d+$/, (match) => parseInt(match) - 1)
          );

          // Apply different styling to the cell above "Bookings"
          cellAbove.font = { bold: true }; // Bold font

          // Change background color
          cellAbove.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFDDDDDD" }, // Change to light grey background
          };
          cellAbove.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };

          // Set alignment to center horizontally and vertically
          cellAbove.alignment = {
            horizontal: "center", // Horizontally center the content
            vertical: "middle", // Vertically center the content
          };

          // Change cell height to make it larger
          const rowIndex = parseInt(cellAbove.address.match(/\d+/)[0]); // Get row index
          worksheet.getRow(rowIndex).height = 30; // Set row height to 30 (you can adjust this value)

          // Merge the cell above "Bookings" with the next three cells in the same row
          const startColumn = cellAbove.col; // Starting column of the cell above "Bookings"
          const endColumn = startColumn + 3; // End column to merge (next three cells)
          worksheet.mergeCells(rowIndex, startColumn, rowIndex, endColumn); // Merge cells

          // Optionally, set the width of the column
          // worksheet.getColumn(cellAbove.col).width = 25;
        }
      });
    });

    // Style for FTD, MTD, YTD
    const valuesToStyle1 = ["FTD", "MTD", "YTD"];
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        if (valuesToStyle1.includes(cell.value)) {
          cell.font = { bold: true };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFDDDDDD" },
          };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          row.height = 25;
        }
      });
    });

    // Generate a buffer from the workbook
    const buffer = await workbook.xlsx.writeBuffer();

    // Create a Blob from the buffer
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Create a link element to download the file
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = "Property_Report.xlsx";

    // Programmatically click the link to trigger the download
    link.click();

    // Clean up the URL object
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page-body">
      <Breadcrumb
        title={strings.projectDetailsReport}
        parentListing={null}
        titleactive={strings.projectDetailsReport}
      />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12 col-xl-12">
            <div className="card">
              <div className="card-body">
                <form className="theme-form">
                  <div className="row">
                    <div className="col-md-4">
                      <span className="cardText">
                        {strings.onDate} (Default: current date)
                      </span>
                      <input
                        className="form-control"
                        type="date"
                        name="on_date" // Changed name to on_date
                        value={form.on_date}
                        onChange={handleInputField}
                      />
                      {formValid.on_date && (
                        <span className="text-danger">{formValid.on_date}</span>
                      )}
                    </div>
                    <div className="col-md-4">
                      <span className="cardText">{strings.propertyName}</span>
                      <Select
                        isSearchable
                        isClearable
                        options={propertyList}
                        getOptionLabel={(option) => option.property_title} // Display property title
                        getOptionValue={(option) => option._id} // Use _id as the unique key
                        value={
                          form.property_id
                            ? propertyList.find(
                                (p) => p._id === form.property_id
                              )
                            : null
                        } // Set the value based on property_id
                        onChange={(e) => {
                          setForm({
                            ...form,
                            property_id: e ? e._id : null, // Update property_id on change
                          });
                        }}
                        placeholder="Select property"
                      />
                    </div>

                    <div className="col-md-4 pt-4">
                      <div className="form-group d-flex justify-content-end">
                        {/* Conditionally render Submit button based on isSubmitted */}

                        <button
                          className="btn btn-warning mr-4"
                          type="button"
                          onClick={filterDetail}
                        >
                          {strings.submit}
                        </button>

                        <button
                          className="btn btn-dark"
                          type="button"
                          onClick={filterReset}
                        >
                          {strings.reset}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <hr />

            <div className="d-flex justify-content-end mb-4">
              <button className="btn btn-primary" onClick={exportToExcel}>
                Export to Excel
              </button>
            </div>

            <div className="mt-4">
              <div className="">
                {propertyData?.length > 0 ? (
                  propertyData
                    .sort((a, b) =>
                      a.property_name.localeCompare(b.property_name)
                    )
                    .map((property) => {
                      const ftdBookingData = property.bookings[0]?.ftd || {
                        GrossBookings: 0,
                        Cancellation: 0,
                        NetBookings: 0,
                      };
                      const mtdBookingData = property.bookings[0]?.mtd || {
                        GrossBookings: 0,
                        Cancellation: 0,
                        NetBookings: 0,
                      };
                      const ytdBookingData = property.bookings[0]?.ytd || {
                        GrossBookings: 0,
                        Cancellation: 0,
                        NetBookings: 0,
                      };

                      const ftdSiteVisitData = property.siteVisits[0]?.ftd || {
                        SiteVisits: 0,
                        Revisits: 0,
                        DirectWalkInVisitsCount: 0,
                        ReferralPartnerVisitsCount: 0,
                        ReferenceVisitsCount: 0,
                        ChannelPartnerVisitsCount: 0,
                        PreSalesVisitsCount: 0,
                        ExhibitionVisitsCount: 0,
                        ChannelPartnerRevisitsCount: 0,
                        DirectWalkInRevisitsCount: 0,
                        ReferenceRevisitsCount: 0,
                        ReferralPartnerRevisitsCount: 0,
                        PreSalesRevisitsCount: 0,
                        ExhibitionRevisitsCount: 0,
                        OtherVisitsCount: 0,
                        OtherRevisitsCount: 0,
                      };
                      const mtdSiteVisitData = property.siteVisits[0]?.mtd || {
                        SiteVisits: 0,
                        Revisits: 0,
                        DirectWalkInVisitsCount: 0,
                        ReferralPartnerVisitsCount: 0,
                        ReferenceVisitsCount: 0,
                        ChannelPartnerVisitsCount: 0,
                        PreSalesVisitsCount: 0,
                        ExhibitionVisitsCount: 0,
                        ChannelPartnerRevisitsCount: 0,
                        DirectWalkInRevisitsCount: 0,
                        ReferenceRevisitsCount: 0,
                        ReferralPartnerRevisitsCount: 0,
                        PreSalesRevisitsCount: 0,
                        ExhibitionRevisitsCount: 0,
                        OtherVisitsCount: 0,
                        OtherRevisitsCount: 0,
                      };
                      const ytdSiteVisitData = property.siteVisits[0]?.ytd || {
                        SiteVisits: 0,
                        Revisits: 0,
                        DirectWalkInVisitsCount: 0,
                        ReferralPartnerVisitsCount: 0,
                        ReferenceVisitsCount: 0,
                        ChannelPartnerVisitsCount: 0,
                        PreSalesVisitsCount: 0,
                        ExhibitionVisitsCount: 0,
                        ChannelPartnerRevisitsCount: 0,
                        DirectWalkInRevisitsCount: 0,
                        ReferenceRevisitsCount: 0,
                        ReferralPartnerRevisitsCount: 0,
                        PreSalesRevisitsCount: 0,
                        ExhibitionRevisitsCount: 0,
                        OtherVisitsCount: 0,
                        OtherRevisitsCount: 0,
                      };

                      return (
                        <div
                          className="card mt-4"
                          style={{ border: "1px solid #a1a1a1" }}
                          key={property.property_name}
                        >
                          <div className="card-body">
                            <div className="mb-4">
                              <h5>
                                Property Title:{" "}
                                <span
                                  style={{ color: "blue", fontWeight: "bold" }}
                                >
                                  {property.property_name}
                                </span>
                              </h5>

                              {/* FTD, MTD, YTD Labels */}
                              <table className="table table-bordered mb-3">
                                <thead>
                                  <tr>
                                    <th className="col-4"></th>
                                    <th className="col-2 text-right">
                                      {strings.ftd}
                                    </th>
                                    <th className="col-2 text-right">
                                      {strings.mtd}
                                    </th>
                                    <th className="col-2 text-right">
                                      {strings.Ytd}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {/* Bookings Section */}
                                  <tr>
                                    <th colSpan="4" className="table-heading">
                                      {strings.bookings}
                                    </th>
                                  </tr>
                                  <tr>
                                    <td>Gross Bookings</td>
                                    <td className="text-right">
                                      {ftdBookingData.GrossBookings}
                                    </td>
                                    <td className="text-right">
                                      {mtdBookingData.GrossBookings}
                                    </td>
                                    <td className="text-right">
                                      {ytdBookingData.GrossBookings}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Cancellations</td>
                                    <td className="text-right">
                                      {ftdBookingData.Cancellation}
                                    </td>
                                    <td className="text-right">
                                      {mtdBookingData.Cancellation}
                                    </td>
                                    <td className="text-right">
                                      {ytdBookingData.Cancellation}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Net Bookings</td>
                                    <td className="text-right">
                                      {ftdBookingData.NetBookings}
                                    </td>
                                    <td className="text-right">
                                      {mtdBookingData.NetBookings}
                                    </td>
                                    <td className="text-right">
                                      {ytdBookingData.NetBookings}
                                    </td>
                                  </tr>

                                  {/* Site Visits Section */}
                                  <tr>
                                    <th colSpan="4" className="table-heading">
                                      {strings.siteVisits}
                                    </th>
                                  </tr>
                                  <tr>
                                    <td>Total Site Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.SiteVisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.SiteVisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.SiteVisits || 0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Channel Partner Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ChannelPartnerVisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ChannelPartnerVisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ChannelPartnerVisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Direct Walk-In Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.DirectWalkInVisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.DirectWalkInVisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.DirectWalkInVisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Referral Partner Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ReferralPartnerVisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ReferralPartnerVisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ReferralPartnerVisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Reference Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ReferenceVisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ReferenceVisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ReferenceVisitsCount ||
                                        0}
                                    </td>
                                  </tr>

                                  <tr>
                                    <td>Pre-Sales Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.PreSalesVisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.PreSalesVisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.PreSalesVisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Exhibition Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ExhibitionVisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ExhibitionVisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ExhibitionVisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Other Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.OtherVisitsCount || 0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.OtherVisitsCount || 0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.OtherVisitsCount || 0}
                                    </td>
                                  </tr>

                                  {/* Revisits Section */}
                                  <tr>
                                    <th colSpan="4" className="table-heading">
                                      {strings.reVisits}
                                    </th>
                                  </tr>
                                  <tr>
                                    <td>Total Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.Revisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.Revisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.Revisits || 0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>ChannelPartner Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ChannelPartnerRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ChannelPartnerRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ChannelPartnerRevisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Direct Walk-In Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.DirectWalkInRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.DirectWalkInRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.DirectWalkInRevisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Referral Partner Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ReferralPartnerRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ReferralPartnerRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ReferralPartnerRevisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Reference Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ReferenceRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ReferenceRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ReferenceRevisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Pre-Sales Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.PreSalesRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.PreSalesRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.PreSalesRevisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Exhibition Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ExhibitionRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ExhibitionRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ExhibitionRevisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Other Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.OtherRevisitsCount || 0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.OtherRevisitsCount || 0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.OtherRevisitsCount || 0}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <TableDataNotFound message="No properties found." />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdrReport;
